import React from 'react';
import { Link } from 'react-router-dom';
import { Mountain, Phone, Mail, MapPin } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { trackContactClick } from '@/lib/trackContactClick';

const Footer: React.FC = () => {
  const { t } = useLanguage();

  const quickLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/sobre-nosotros', label: t('nav.about') },
    { path: '/servicios', label: t('nav.services') },
    { path: '/proyectos', label: t('nav.projects') },
    { path: '/presupuesto', label: t('nav.quote') },
    { path: '/inspeccion-de-rocodromos', label: t('nav.inspection') },
    { path: '/blog', label: t('nav.blog') },
    { path: '/contacto', label: t('nav.contact') },
  ];

  const certifications = [
    'UNE-EN 12572',
    'UNE-EN 15567',
    'UNE-EN 16869',
  ];

  return (
    <footer className="bg-secondary text-secondary-foreground">
      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-2">
              <Mountain className="h-8 w-8 text-primary" />
              <span className="text-2xl font-bold">Rock-Line</span>
            </Link>
            <p className="text-secondary-foreground/80 text-sm leading-relaxed">
              {t('hero.subtitle')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-lg mb-4">{t('footer.quicklinks')}</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-secondary-foreground/80 hover:text-primary transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Certifications */}
          <div>
            <h3 className="font-semibold text-lg mb-4">{t('footer.certifications')}</h3>
            <ul className="space-y-2">
              {certifications.map((cert) => (
                <li key={cert} className="text-secondary-foreground/80 text-sm">
                  {cert}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold text-lg mb-4">{t('nav.contact')}</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="tel:+34669568722"
                  onClick={() => trackContactClick('phone')}
                  className="flex items-center gap-2 text-secondary-foreground/80 hover:text-primary transition-colors text-sm"
                >
                  <Phone className="h-4 w-4" />
                  +34 669 56 87 22
                </a>
              </li>
              <li>
                <a
                  href="mailto:rockline.es@gmail.com"
                  className="flex items-center gap-2 text-secondary-foreground/80 hover:text-primary transition-colors text-sm"
                >
                  <Mail className="h-4 w-4" />
                  rockline.es@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-2 text-secondary-foreground/80 text-sm">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>Irurtzun, Navarra (Sede Central)</span>
              </li>
              <li className="flex items-start gap-2 text-secondary-foreground/80 text-sm">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>Madrid (Delegación)</span>
              </li>
              <li className="flex items-start gap-2 text-secondary-foreground/80 text-sm">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>Valencia (Delegación)</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-secondary-foreground/20 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-secondary-foreground/60 text-sm">
            © {new Date().getFullYear()} Rock-Line. {t('footer.rights')}.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
